import { View, Text, Pressable } from 'react-native';
import { Quote, CheckCircle } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { FounderTestimonial } from '@/lib/state/inverio-store';
import StarRating from './StarRating';

interface TestimonialCardProps {
  testimonial: FounderTestimonial;
  compact?: boolean;
  /** When present, renders a right-aligned "Report" button (App Store Guideline 1.2). */
  onReport?: () => void;
}

/** Right-aligned, 44pt-tall "Report" text button. View owns the height; the Pressable fills it (pressable_sizing). */
function ReportButton({ onReport }: { onReport: () => void }) {
  const colors = useThemeColors();
  return (
    <View style={{ height: 44, alignItems: 'flex-end' }}>
      <Pressable
        onPress={onReport}
        accessibilityRole="button"
        accessibilityLabel="Report this review"
        style={{ flex: 1, paddingHorizontal: 4, justifyContent: 'center' }}
      >
        <Text numberOfLines={1} style={{ color: colors.text.tertiary, fontSize: 12, fontWeight: '600' }}>
          Report
        </Text>
      </Pressable>
    </View>
  );
}

export default function TestimonialCard({
  testimonial,
  compact = false,
  onReport,
}: TestimonialCardProps) {
  const colors = useThemeColors();

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (compact) {
    return (
      <View
        className="rounded-xl p-4"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        <View className="flex-row items-start gap-3">
          {/* Avatar */}
          <View
            className="w-10 h-10 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.primary[100] }}
          >
            <Text
              style={{ color: colors.primary[600] }}
              className="font-bold"
            >
              {testimonial.founderName
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </Text>
          </View>

          {/* Content */}
          <View className="flex-1">
            <View className="flex-row items-center justify-between">
              <View className="flex-row items-center gap-1">
                <Text
                  style={{ color: colors.text.primary }}
                  className="font-medium"
                >
                  {testimonial.founderName}
                </Text>
                {testimonial.verified && (
                  <CheckCircle size={12} color={colors.success} />
                )}
              </View>
              <StarRating rating={testimonial.rating} size="sm" />
            </View>
            <Text
              style={{ color: colors.text.tertiary }}
              className="text-xs mt-0.5"
            >
              {testimonial.companyName}
            </Text>
            <Text
              style={{ color: colors.text.secondary }}
              className="text-sm mt-2"
              numberOfLines={2}
            >
              "{testimonial.text}"
            </Text>
          </View>
        </View>
        {onReport ? <ReportButton onReport={onReport} /> : null}
      </View>
    );
  }

  return (
    <View
      className="rounded-2xl p-5"
      style={{
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      {/* Quote icon */}
      <View
        className="w-8 h-8 rounded-lg items-center justify-center mb-3"
        style={{ backgroundColor: colors.primary[50] }}
      >
        <Quote size={16} color={colors.primary[600]} />
      </View>

      {/* Testimonial text */}
      <Text
        style={{ color: colors.text.secondary }}
        className="text-base leading-6 mb-4"
      >
        "{testimonial.text}"
      </Text>

      {/* Footer */}
      <View className="flex-row items-center justify-between pt-3 border-t" style={{ borderColor: colors.border.light }}>
        <View className="flex-row items-center gap-3">
          {/* Avatar */}
          <View
            className="w-10 h-10 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.primary[100] }}
          >
            <Text
              style={{ color: colors.primary[600] }}
              className="font-bold"
            >
              {testimonial.founderName
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </Text>
          </View>

          {/* Info */}
          <View>
            <View className="flex-row items-center gap-1">
              <Text
                style={{ color: colors.text.primary }}
                className="font-medium"
              >
                {testimonial.founderName}
              </Text>
              {testimonial.verified && (
                <CheckCircle size={14} color={colors.success} />
              )}
            </View>
            <Text
              style={{ color: colors.text.tertiary }}
              className="text-sm"
            >
              {testimonial.companyName} · {formatDate(testimonial.date)}
            </Text>
          </View>
        </View>

        {/* Rating */}
        <StarRating rating={testimonial.rating} size="sm" showValue />
      </View>
      {onReport ? <ReportButton onReport={onReport} /> : null}
    </View>
  );
}
